import React from "react";
import {
  Box,
  Button,
  Typography,
  Grid,
  CircularProgress,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import config from "./../../config";
import DocumentUploadBox from "./DocumentUploadBox";
import { useDocumentManager } from "../../hooks/useDocumentManager";
import { useNavigate } from "react-router";

export function DocumentUploadSection({
  driverId,
  documents: propDocuments,
  setDocuments: setPropDocuments,
  isEditing,
  onComplete,
  orgId,
  onSubmit,
  onCancel,
  isDetailsFormValid,
}) {
  const { t } = useTranslation();
  const navigate=useNavigate();
  const documentTypes = [
    "Photo ID",
    "Driving License",
    "Address Proof",
    "Other",
  ];
  const specificTypes = ["Photo ID", "Driving License", "Address Proof"];
  const maxOtherDocuments = 3;
  // console.log("driverId", driverId);
  const {
    documents,
    isLoading,
    isSubmitting,
    alert,
    errors,
    touched,
    confirmModal,
    setAlert,
    setTouched,
    canAddDocument,
    getRemainingOtherDocs,
    getAvailableDocTypes,
    handleAddDocument,
    handleRemoveDocument,
    handleCancelRemove,
    handleConfirmRemove,
    handleDocTypeChange,
    handleFileUpload,
    handleDownload,
    handleSave,
  } = useDocumentManager({
    entityId: driverId,
    entityType: "driver",
    documents: propDocuments,
    setDocuments: setPropDocuments,
    isEditing,
    orgId,
    documentTypes,
    specificTypes,
    maxOtherDocuments,
    apiEndpoint: "/driver/id",
    config,
    onSubmit,
    onComplete,
  });

  return (
    <>
      <Box
        sx={{
          backgroundColor: isEditing ? "#B7BFCC" : "#647490",
          display: "flex",
          borderRadius: "8px",
          padding: "12px 24px",
          flexDirection: { xs: "column", md: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", md: "center" },
          mb: 4,
          boxShadow: "-2px 2px 5px 0px #00000040",
          mt: 3,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 500, fontSize: "24px", fontFamily: "Roboto", color: isEditing ? "#1F242E" : "#FFFFFF" }}>
          {t("Upload Documents")} ({t("Photo ID")}, {t("Driving License")},{" "}
          {t("Address Proof")})
          <Typography
            variant="body2"
            component="span"
            sx={{ ml: 0.5, color: "#ff8a80" }}
          >
            *
          </Typography>
        </Typography>
      </Box>

      {isLoading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: 200,
          }}
        >
          <CircularProgress size={48} />
        </Box>
      ) : (
        <Grid container spacing={3}>
          {documents.map((doc, index) => (
            <Grid item xs={12} sm={4} key={doc.id}>
              <Box sx={{ position: "relative" }}>
                <DocumentUploadBox
                  isFirstDocument={index === 0} // Pass true for first document, false for others
                  documentType={doc.type}
                  onDocumentTypeChange={(e) =>
                    handleDocTypeChange(doc.id, e.target.value)
                  }
                  onFileChange={(e) => handleFileUpload(doc.id, e)}
                  selectedFile={
                    doc.file || (doc.isUploaded ? { name: doc.fileName } : null)
                  }
                  preview={doc.preview}
                  isUploaded={doc.isUploaded}
                  needsReplacement={doc.needsReplacement}
                  onDownload={() => handleDownload(doc)}
                  isSubmitting={isSubmitting}
                  docId={doc.id}
                  errorType={errors[doc.id]?.type}
                  errorFile={errors[doc.id]?.file}
                  touchedType={(value) =>
                    setTouched((prev) => ({
                      ...prev,
                      [doc.id]: { ...prev[doc.id], type: value },
                    }))
                  }
                  touchedFile={(value) =>
                    setTouched((prev) => ({
                      ...prev,
                      [doc.id]: { ...prev[doc.id], file: value },
                    }))
                  }
                  availableDocTypes={
                    getAvailableDocTypes(doc.id).availableTypes
                  }
                  usedDocTypes={getAvailableDocTypes(doc.id).usedTypes}
                  onRemove={() => handleRemoveDocument(doc.id)} // Pass the delete handler
                  hasFile={doc.isUploaded || doc.file} // Pass the condition for showing the button
                  canAddDocument={canAddDocument}
                />
              </Box>
            </Grid>
          ))}
          {canAddDocument() && (
            <Grid item xs={12} sm={4}>
              <Box
                sx={{
                  height: 350,
                  border: "2px dashed #1976D2",
                  borderRadius: "8px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  backgroundColor: "#E3F2FD",
                  cursor: "pointer",
                }}
                onClick={handleAddDocument}
              >
                <Box sx={{ textAlign: "center" }}>
                  <AddIcon sx={{ fontSize: 48, color: "#1976D2", mb: 2 }} />
                  <Typography variant="h6" sx={{ fontWeight: 500 }}>
                    {t("Add New Document")}
                  </Typography>
                  {getRemainingOtherDocs() > 0 && (
                    <Typography
                      variant="caption"
                      sx={{ mt: 1, color: "#1976D2" }}
                    >
                      {t(
                        `${getRemainingOtherDocs()} Other document${
                          getRemainingOtherDocs() !== 1 ? "s" : ""
                        } remaining`
                      )}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Grid>
          )}
        </Grid>
      )}

      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          gap: "20px",
          marginTop: "40px",
        }}
      >

        <Button
          onClick={() => {
              handleSave(isDetailsFormValid);
          }}
          disabled={isSubmitting || !isDetailsFormValid}
          sx={{
            fontWeight: 600,
            height: "55px",
            fontSize: "20px",
            color: "#111214",
            width: "122px",
            textTransform: "none",
            borderRadius: "8px",
            backgroundColor: "#F9E719",
            border: "1px solid #414958",
            "&:hover": {
              backgroundColor: "#F5E016",
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
              transform: "translateY(-1px)",
              border: "1px solid #2C3441",
            },
          }}
        >
          {isSubmitting ? `${t("Submitting")}...` : t("Save")}
        </Button>
      </Box>

      <Snackbar
        open={alert.open}
        autoHideDuration={6000}
        onClose={() => setAlert({ ...alert, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setAlert({ ...alert, open: false })}
          severity={alert.severity}
          sx={{ width: "100%" }}
        >
          {alert.message}
        </Alert>
      </Snackbar>
      <Dialog
        open={confirmModal.open}
        onClose={handleCancelRemove}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        PaperProps={{
          sx: { borderRadius: "10px", boxShadow: "-2px 2px 5px 0px #00000040" },
        }}
      >
        <DialogTitle
          id="alert-dialog-title"
          sx={{ bgcolor: "#F2F2F3", color: "#414958" }}
        >
          {t("Confirm Document Removal")}
        </DialogTitle>
        <DialogContent sx={{ p: 3, mt: 1 }}>
          <DialogContentText id="alert-dialog-description">
            {t(
              "Are you sure you want to remove this document? This action cannot be undone."
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={handleCancelRemove}
            sx={{
              color: "#1F242E",
              borderRadius: "8px",
              textTransform: "none",
              padding: "8px 16px",
              background: "#F2F2F3",
              border: "1px solid #414958",
            }}
          >
            {t("Cancel")}
          </Button>
          <Button
            onClick={handleConfirmRemove}
            sx={{
              color: "#FFFFFF",
              borderRadius: "8px",
              textTransform: "none",
              padding: "8px 16px",
              background: "#d32f2f",
              "&:hover": { background: "#b71c1c" },
            }}
            autoFocus
          >
            {t("Remove")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
